import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type { CartItem, Product } from '../types/product';
import { branchByKey, MAIN_BRANCH, type Branch } from '../config/branches';
import { branchWhatsApp, useBranch } from './BranchContext';
import { fullProductName } from '../utils/productName';

/** Productos del carrito que se piden a una misma sucursal. */
export interface CartGroup {
  branch: Branch;
  items: CartItem[];
  total: number;
}

interface CartContextValue {
  items: CartItem[];
  /** Una entrada por sucursal, en el orden en que se agregaron. */
  groups: CartGroup[];
  drawerOpen: boolean;
  toastItem: string | null;
  /** Agrega a la sucursal elegida en ese momento. */
  addItem: (product: Product) => void;
  removeItem: (id: string | number, branchKey?: string) => void;
  updateQuantity: (id: string | number, quantity: number, branchKey?: string) => void;
  clearCart: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  totalItems: number;
  totalPrice: number;
  /** Sin sucursal: pide todo (si todo es de una sola) o la primera. */
  checkoutViaWhatsApp: (branchKey?: string) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

// El carrito se guarda en el navegador y caduca a los 3 dias sin cambios.
const STORAGE_KEY = 'spartech_cart';
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const saved = JSON.parse(raw) as { items: CartItem[]; updatedAt: number };
    if (!Array.isArray(saved.items) || Date.now() - saved.updatedAt > MAX_AGE_MS) return [];
    return saved.items;
  } catch {
    return [];
  }
}

// Carritos guardados antes de haber sucursales no traen branchKey: son de la principal.
const keyOf = (item: Pick<CartItem, 'branchKey'>) => item.branchKey ?? MAIN_BRANCH.key;
const sameLine = (item: CartItem, id: string | number, branchKey?: string) =>
  item.id === id && keyOf(item) === (branchKey ?? MAIN_BRANCH.key);

export function CartProvider({ children }: { children: ReactNode }) {
  const { branch } = useBranch();
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    try {
      if (items.length === 0) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, updatedAt: Date.now() }));
    } catch {
      // almacenamiento no disponible (modo privado): el carrito vive solo en memoria
    }
  }, [items]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toastItem, setToastItem] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addItem = useCallback((product: Product) => {
    const branchKey = branch.key;
    setItems((prev) => {
      const existing = prev.find((i) => sameLine(i, product.id, branchKey));
      if (existing) {
        return prev.map((i) =>
          sameLine(i, product.id, branchKey) ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...product, quantity: 1, branchKey }];
    });
    // Show toast but don't auto-open drawer
    if (toastTimer.current) clearTimeout(toastTimer.current);
    const shortName = product.name.length > 32 ? product.name.slice(0, 32) + '…' : product.name;
    setToastItem(shortName);
    toastTimer.current = setTimeout(() => setToastItem(null), 2800);
  }, [branch.key]);

  const removeItem = useCallback((id: string | number, branchKey?: string) => {
    setItems((prev) => prev.filter((i) => !sameLine(i, id, branchKey)));
  }, []);

  const updateQuantity = useCallback((id: string | number, quantity: number, branchKey?: string) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => !sameLine(i, id, branchKey)));
    } else {
      setItems((prev) => prev.map((i) => (sameLine(i, id, branchKey) ? { ...i, quantity } : i)));
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + (i.price ?? 0) * i.quantity, 0);

  const groups = useMemo<CartGroup[]>(() => {
    const map = new Map<string, CartGroup>();
    for (const item of items) {
      const key = keyOf(item);
      if (!map.has(key)) map.set(key, { branch: branchByKey(key), items: [], total: 0 });
      const g = map.get(key)!;
      g.items.push(item);
      g.total += (item.price ?? 0) * item.quantity;
    }
    return [...map.values()];
  }, [items]);

  const checkoutViaWhatsApp = useCallback((branchKey?: string) => {
    const group = branchKey ? groups.find((g) => g.branch.key === branchKey) : groups[0];
    if (!group) return;
    const lines = group.items
      .map(
        (i) =>
          `• ${fullProductName(i)} x${i.quantity}${
            i.price
              ? ` — RD$${(i.price * i.quantity).toLocaleString('es-DO')}`
              : ''
          }`
      )
      .join('\n');
    const totalStr =
      group.total > 0
        ? `\n\n💰 *Total estimado:* RD$${group.total.toLocaleString('es-DO')}`
        : '';
    // Con una sola sucursal el mensaje queda como siempre.
    const where = groups.length > 1 || group.branch.key !== MAIN_BRANCH.key ? ` (${group.branch.name})` : '';
    const msg = `¡Hola! Quiero realizar el siguiente pedido en SpartechRD${where}:\n\n🛒 *Mi carrito:*\n${lines}${totalStr}\n\nPor favor, ¿me pueden ayudar a finalizar mi compra?`;
    window.open(
      `https://wa.me/${branchWhatsApp(group.branch)}?text=${encodeURIComponent(msg)}`,
      '_blank'
    );
  }, [groups]);

  return (
    <CartContext.Provider
      value={{
        items,
        groups,
        drawerOpen,
        toastItem,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        openDrawer,
        closeDrawer,
        totalItems,
        totalPrice,
        checkoutViaWhatsApp,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
